import React, { useState, useEffect } from 'react';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { hashtag_data_by_category } from './hashtag_data';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

interface FilterSectionProps {
  onPriceRangeChange: (range: [number, number]) => void;
  onSearchTermChange: (term: string) => void;
  onStarRatingChange: (rating: number) => void;
  onDiscountChange: (discounted: boolean | null) => void;
  onHashtagChange: (hashtags: string[]) => void;
  category: string;
}

export default function FilterSection({
  onPriceRangeChange,
  onSearchTermChange,
  onStarRatingChange,
  onDiscountChange,
  onHashtagChange,
  category,
}: FilterSectionProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 200000]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStar, setSelectedStar] = useState(1);
  const [isDiscounted, setIsDiscounted] = useState<null | boolean>(null);
  const [selectedHashtags, setSelectedHashtags] = useState<string[]>([]);

  const hashtags = hashtag_data_by_category[category] || [];
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);


  const handleFilterButtonClick = () => {
    if (isOpen) {
      setIsAnimating(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsAnimating(false);
      }, 300);
    } else {
      setTimeout(() => {
        setIsOpen(true);
      }, 50); // 약간의 지연을 추가하여 강제 리렌더링 유도
    }
  };
  

  // 쿼리 파라미터에서 해시태그 추출 및 초기화
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const subcategory = params.get('subcategory');

    // URL에 해당하는 해시태그가 있다면 자동으로 선택 상태로 설정
    if (subcategory && hashtags.includes(subcategory)) {
      setSelectedHashtags([subcategory]);
      setActiveDropdown('hashtag');
    }
  }, [location.search, hashtags]);

  useEffect(() => {
    onPriceRangeChange(priceRange);
  }, [priceRange]);

  useEffect(() => {
    onSearchTermChange(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    onStarRatingChange(selectedStar);
  }, [selectedStar]);

  useEffect(() => {
    onDiscountChange(isDiscounted);
  }, [isDiscounted]);

  useEffect(() => {
    onHashtagChange(selectedHashtags);
  }, [selectedHashtags]);

  const handleDropdownToggle = (dropdown: string) => {
    setActiveDropdown((prev) => (prev === dropdown ? null : dropdown));
  };

  const handlePriceChange = (value: number | number[]) => {
    if (Array.isArray(value) && value.length === 2) {
      setPriceRange([value[0], value[1]]);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleStarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedStar(parseInt(e.target.value));
  };

  const handleDiscountChange = (discounted: boolean | null) => {
    setIsDiscounted(discounted);
  };

  const handleHashtagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setSelectedHashtags((prevHashtags) =>
      checked
        ? [...prevHashtags.filter((tag) => tag !== value), value]
        : prevHashtags.filter((tag) => tag !== value)
    );
  };


  const resetFilters = () => {
    setPriceRange([0, 200000]);
    setSearchTerm('');
    setSelectedStar(1);
    setIsDiscounted(null);
    setSelectedHashtags([]);
    setActiveDropdown(null);
  };

  useEffect(() => {
    if (isOpen) {
      // 모달이 열렸을 때 body 스크롤을 비활성화
      document.body.style.overflow = 'hidden';
    } else {
      // 모달이 닫혔을 때 body 스크롤을 복구
      document.body.style.overflow = 'auto';
    }
  
    // 컴포넌트가 언마운트될 때 스크롤 복구
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  return (
    <>
      {/* pc */}
      <aside
        className={`col-span-1 bg-gray-100 p-4 rounded-lg h-[700px] ${
          isMobile ? 'hidden' : 'block'
        }`}
      >
        <h3 className="font-bold text-lg mb-4">{t('filterPanel.title')}</h3>

        <div className="mb-6">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={t('filterPanel.search')}
            className="w-full p-2 border border-gray-300 rounded-lg"
          />
        </div>

        {/* 정렬 기준 드롭다운 */}
        <div className="border-b mb-4">
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() => handleDropdownToggle('sort')}
          >
            <h4 className="text-base font-medium pb-2">
              {t('filterPanel.sort.title')}
            </h4>
            {activeDropdown === 'sort' ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </div>
          {activeDropdown === 'sort' && (
            <ul className="mb-3 space-y-2">
              <li className="h-[30px] flex items-center">
                <input type="radio" name="sort" id="sort1" />
                <label htmlFor="sort1" className="ml-2">
                  {t('filterPanel.sort.recommend')}
                </label>
              </li>
              <li className="h-[30px] flex items-center">
                <input type="radio" name="sort" id="sort2" />
                <label htmlFor="sort2" className="ml-2">
                  {t('filterPanel.sort.rating')}
                </label>
              </li>
              <li className="h-[30px] flex items-center">
                <input type="radio" name="sort" id="sort3" />
                <label htmlFor="sort3" className="ml-2">
                  {t('filterPanel.sort.new')}
                </label>
              </li>
            </ul>
          )}
        </div>

        {/* 좋아요 기준 드롭다운 */}
        {/* <div className="border-b mb-4">
                  <div
                      className="flex justify-between items-center cursor-pointer"
                      onClick={() => handleDropdownToggle('star')}
                  >
                      <h4 className="text-base font-medium pb-2">
                          {t('filterPanel.likes.title')}
                      </h4>
                      {activeDropdown === 'star' ? <IoIosArrowUp /> : <IoIosArrowDown />}
                  </div>
                  {activeDropdown === 'star' && (
                      <ul className="mb-3 space-y-2">
                          <li className='h-[30px] flex items-center'>
                              <input
                                  type="radio"
                                  name="star"
                                  value="5"
                                  checked={selectedStar === 5}
                                  onChange={handleStarChange}
                              />
                              <label htmlFor="star5" className="ml-2">
                                  {t('filterPanel.likes.five')}
                              </label>
                          </li>
                          <li className='h-[30px] flex items-center'>
                              <input
                                  type="radio"
                                  name="star"
                                  value="4"
                                  checked={selectedStar === 4}
                                  onChange={handleStarChange}
                              />
                              <label htmlFor="star4" className="ml-2">
                                  {t('filterPanel.likes.four_or_more')}
                              </label>
                          </li>
                          <li className='h-[30px] flex items-center'>
                              <input
                                  type="radio"
                                  name="star"
                                  value="3"
                                  checked={selectedStar === 3}
                                  onChange={handleStarChange}
                              />
                              <label htmlFor="star3" className="ml-2">
                                  {t('filterPanel.likes.three_or_more')}
                              </label>
                          </li>
                          <li className='h-[30px] flex items-center'>
                              <input
                                  type="radio"
                                  name="star"
                                  value="2"
                                  checked={selectedStar === 2}
                                  onChange={handleStarChange}
                              />
                              <label htmlFor="star2" className="ml-2">
                                  {t('filterPanel.likes.two_or_more')}
                              </label>
                          </li>
                          <li className='h-[30px] flex items-center'>
                              <input
                                  type="radio"
                                  name="star"
                                  value="1"
                                  checked={selectedStar === 1}
                                  onChange={handleStarChange}
                              />
                              <label htmlFor="star1" className="ml-2">
                                  {t('filterPanel.likes.one_or_more')}
                              </label>
                          </li>
                      </ul>
                  )}
              </div> */}

        {/* 할인 여부 필터 드롭다운 */}
        <div className="border-b mb-4">
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() => handleDropdownToggle('discount')}
          >
            <h4 className="text-base font-medium pb-2">
              {t('filterPanel.discount.title')}
            </h4>
            {activeDropdown === 'discount' ? (
              <IoIosArrowUp />
            ) : (
              <IoIosArrowDown />
            )}
          </div>
          {activeDropdown === 'discount' && (
            <ul className="mb-3 space-y-2">
              <li className="h-[30px] flex items-center">
                <input
                  type="radio"
                  name="discount"
                  value="all"
                  checked={isDiscounted === null}
                  onChange={() => handleDiscountChange(null)}
                />
                <label htmlFor="discount-all" className="ml-2">
                  {t('filterPanel.discount.all')}
                </label>
              </li>
              <li className="h-[30px] flex items-center">
                <input
                  type="radio"
                  name="discount"
                  value="yes"
                  checked={isDiscounted === true}
                  onChange={() => handleDiscountChange(true)}
                />
                <label htmlFor="discount-yes" className="ml-2">
                  {t('filterPanel.discount.discounted')}
                </label>
              </li>
              <li className="h-[30px] flex items-center">
                <input
                  type="radio"
                  name="discount"
                  value="no"
                  checked={isDiscounted === false}
                  onChange={() => handleDiscountChange(false)}
                />
                <label htmlFor="discount-no" className="ml-2">
                  {t('filterPanel.discount.not_discounted')}
                </label>
              </li>
            </ul>
          )}
        </div>

        {/* 해시태그 필터 드롭다운 */}
        <div className="border-b mb-4">
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() => handleDropdownToggle('hashtag')}
          >
            <h4 className="text-base font-medium pb-2">
              {t('filterPanel.hashtags.title')}
            </h4>
            {activeDropdown === 'hashtag' ? (
              <IoIosArrowUp />
            ) : (
              <IoIosArrowDown />
            )}
          </div>
          {activeDropdown === 'hashtag' && (
            <ul className="mb-3 space-y-2">
              {hashtags.map((hashtag) => (
                <li key={hashtag} className="h-[30px] flex items-center">
                  <input
                    type="checkbox"
                    value={hashtag}
                    checked={selectedHashtags.includes(hashtag)}
                    onChange={handleHashtagChange}
                  />
                  <label htmlFor={hashtag} className="ml-2">
                    {hashtag}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* 가격 범위 슬라이더 */}
        <div className="mt-6">
          <h4 className="text-base font-medium mb-2">
            {t('filterPanel.price_range.title')}
          </h4>
          <Slider
            range
            min={0}
            max={200000}
            value={priceRange}
            onChange={handlePriceChange}
            step={1000}
            trackStyle={{ backgroundColor: '#fc1c49', height: 5 }}
            handleStyle={[
              { borderColor: '#fc1c49', height: 15, width: 15 },
              { borderColor: '#fc1c49', height: 15, width: 15 },
            ]}
          />
          <div className="flex justify-between mt-2 text-sm text-gray-600">
            <span>{priceRange[0].toLocaleString('ko-KR')}</span>
            <span>{priceRange[1].toLocaleString('ko-KR')}</span>
          </div>
        </div>
        <button
          onClick={resetFilters}
          className="bg-black hover:bg-opacity-70 transition duration-300 ease-in-out mt-[25px] w-full text-white p-3 mb-4 rounded-lg"
        >
          {t('filterPanel.reset')}
        </button>
      </aside>

      {/* mobile */}
      {isMobile && (
        <>
          {/* 필터 버튼  */}
            <div className="filter-btn-container absolute right-0 top-[-22px]">
              <button
                type="button"
                onClick={handleFilterButtonClick}
              >
                
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={22}
                  height={22}
                  viewBox="0 0 21 21"
                >
                  <path
                    fill="none"
                    stroke="#1f1f1f"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.5 4a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0V5a1 1 0 0 1 1-1m12 2h-11m-2 0h-3m4 8a1 1 0 0 1 1 1v2a1 1 0 0 1-2 0v-2a1 1 0 0 1 1-1m12 2h-11m-2 0h-3m12-7a1 1 0 0 1 1 1v2a1 1 0 0 1-2 0v-2a1 1 0 0 1 1-1m-1 2h-11m16 0h-3"
                  ></path>
                </svg>
                {/* {t('filterPanel.title')} */}
              </button>
            </div>
          {/* 필터 패널 */}
          {isOpen && (
            <div className="fixed inset-0 z-[10002] bg-white overflow-y-auto">
              <div
                className={`bg-white rounded-t-lg w-full max-w-md p-6 transition-transform transform ${
                  isOpen && !isAnimating
                    ? 'animate-slide-up'
                    : 'animate-slide-down'
                }`}
              >
                
                <div className='pb-4'>
                  <span className="text-lg text-[#1f1f1f] Pretendard-Bold tracking-wide">
                    {t('filterPanel.title')}
                  </span>
                </div>

                <div className='border-t'>
                  {/* 검색 */}
                  <div className="mt-8">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      placeholder={t('filterPanel.search')}
                      className="w-full p-3 border Pretendard-Regular border-[#e2e2e2] tracking-wide text-[#1f1f1f] placeholder:text-[#afafaf] text-[13px] rounded-lg"
                    />
                  </div>

                  {/* 정렬 기준 드롭다운 */}
                  <div className="mt-8">
                    <div
                      className="flex justify-between text-[#1f1f1f] items-center cursor-pointer"
                      onClick={() => handleDropdownToggle('sort')}
                    >
                      <h4 className="text-sm text-[#141414] Pretendard-Medium pb-2">
                        {t('filterPanel.sort.title')}
                      </h4>
                      {activeDropdown === 'sort' ? (
                        <IoIosArrowUp />
                      ) : (
                        <IoIosArrowDown />
                      )}
                    </div>
                    {activeDropdown === 'sort' && (
                      <ul className=" mb-3 space-y-2 text-[#333] text-xs">
                        <li className="h-[24px] flex items-center">
                          <input type="radio" name="sort" id="sort1" />
                          <label htmlFor="sort1" className="ml-2">
                            {t('filterPanel.sort.recommend')}
                          </label>
                        </li>
                        <li className="h-[24px] flex items-center">
                          <input type="radio" name="sort" id="sort2" />
                          <label htmlFor="sort2" className="ml-2">
                            {t('filterPanel.sort.rating')}
                          </label>
                        </li>
                        <li className="h-[24px] flex items-center">
                          <input type="radio" name="sort" id="sort3" />
                          <label htmlFor="sort3" className="ml-2">
                            {t('filterPanel.sort.new')}
                          </label>
                        </li>
                      </ul>
                    )}
                  </div>

                  {/* 할인 여부 필터 드롭다운 */}
                  <div className="mt-8">
                    <div
                      className="flex justify-between text-[#1f1f1f] items-center cursor-pointer"
                      onClick={() => handleDropdownToggle('discount')}
                    >
                      <h4 className="text-sm text-[#141414] Pretendard-Medium pb-2">
                        {t('filterPanel.discount.title')}
                      </h4>
                      {activeDropdown === 'discount' ? (
                        <IoIosArrowUp />
                      ) : (
                        <IoIosArrowDown />
                      )}
                    </div>
                    {activeDropdown === 'discount' && (
                      <ul className="mb-3 space-y-2 text-[#1f1f1f] text-xs">
                        <li className="h-[24px] flex items-center">
                          <input
                            type="radio"
                            name="discount"
                            value="all"
                            checked={isDiscounted === null}
                            onChange={() => handleDiscountChange(null)}
                          />
                          <label htmlFor="discount-all" className="ml-2">
                            {t('filterPanel.discount.all')}
                          </label>
                        </li>
                        <li className="h-[24px] flex items-center">
                          <input
                            type="radio"
                            name="discount"
                            value="yes"
                            checked={isDiscounted === true}
                            onChange={() => handleDiscountChange(true)}
                          />
                          <label htmlFor="discount-yes" className="ml-2">
                            {t('filterPanel.discount.discounted')}
                          </label>
                        </li>
                        <li className="h-[24px] flex items-center">
                          <input
                            type="radio"
                            name="discount"
                            value="no"
                            checked={isDiscounted === false}
                            onChange={() => handleDiscountChange(false)}
                          />
                          <label htmlFor="discount-no" className="ml-2">
                            {t('filterPanel.discount.not_discounted')}
                          </label>
                        </li>
                      </ul>
                    )}
                  </div>

                  {/* 해시태그 필터 드롭다운 */}
                  <div className="mt-8">
                    <div
                      className="flex justify-between text-[#1f1f1f] items-center cursor-pointer"
                      onClick={() => handleDropdownToggle('hashtag')}
                    >
                      <h4 className="text-sm text-[#141414] Pretendard-Medium pb-2">
                        {t('filterPanel.hashtags.title')}
                      </h4>
                      {activeDropdown === 'hashtag' ? (
                        <IoIosArrowUp />
                      ) : (
                        <IoIosArrowDown />
                      )}
                    </div>
                    {activeDropdown === 'hashtag' && (
                      <ul className="mb-3 space-y-2 text-[#1f1f1f] text-xs">
                        {hashtags.map((hashtag) => (
                          <li
                            key={hashtag}
                            className="h-[24px] flex items-center"
                          >
                            <input
                              type="radio"
                              value={hashtag}
                              checked={selectedHashtags.includes(hashtag)}
                              onChange={handleHashtagChange}
                            />
                            <label htmlFor={hashtag} className="ml-2">
                              {hashtag}
                            </label>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* 가격 범위 슬라이더 */}
                  <div className="mt-8">
                    <h4 className="text-sm text-[#141414] Pretendard-Medium pb-4">
                      {t('filterPanel.price_range.title')}
                    </h4>
                    <Slider
                      range
                      min={0}
                      max={200000}
                      value={priceRange}
                      onChange={handlePriceChange}
                      step={1000}
                      trackStyle={{ backgroundColor: '#333', height: 3 }}
                      handleStyle={[
                        { borderColor: '#333', height: 15, width: 15 },
                        { borderColor: '#333', height: 15, width: 15 },
                      ]}
                    />
                    <div className="flex justify-between Pretendard-Bold mt-[6px] text-[11px] text-[#1f1f1f] ">
                      <span>{priceRange[0].toLocaleString('ko-KR')}</span>
                      <span>{priceRange[1].toLocaleString('ko-KR')}</span>
                    </div>
                  </div>
                </div>
                {/* 초기화 */}
                <button
                  onClick={resetFilters}
                  className="bg-[#141414] hover:bg-opacity-70 transition duration-300 ease-in-out mt-[25px] w-full text-white Pretendard-Bold tracking-wide p-3 rounded-lg"
                >
                  {t('filterPanel.reset')}
                </button>
                {/* 닫기 */}
                <button
                  onClick={handleFilterButtonClick}
                  className="absolute top-8 right-4"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                  >
                    <g fill="none" fillRule="evenodd">
                      <path d="m12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035q-.016-.005-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093q.019.005.029-.008l.004-.014l-.034-.614q-.005-.018-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z"></path>
                      <path
                        fill="#757575"
                        d="m12 14.122l5.303 5.303a1.5 1.5 0 0 0 2.122-2.122L14.12 12l5.304-5.303a1.5 1.5 0 1 0-2.122-2.121L12 9.879L6.697 4.576a1.5 1.5 0 1 0-2.122 2.12L9.88 12l-5.304 5.304a1.5 1.5 0 1 0 2.122 2.12z"
                      ></path>
                    </g>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
